import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type ApiDescriptor,
  type ApiServiceBaseOperationDescriptor,
  type ApiServiceDescriptor,
  CloudflowEngine,
  CloudFlowProvider,
} from "@doitintl/cmp-models";
import { type DocumentData, getCollection } from "@doitintl/models-any";
import { type TransformMethod, useCollectionDataOnce, useDocumentData } from "@doitintl/models-hooks";

import { type PaginatedQueryProps, usePaginatedQuery } from "../../../../../Components/hooks/usePaginatedQuery";
import { type OnOperationSelectFn } from "../ActionSearchDialog";
import { popularServices } from "../consts";
import { type Operation, useOperationsQuery } from "./useOperationsQuery";

type ActiveQuery<T extends DocumentData = DocumentData> = PaginatedQueryProps<T, undefined, undefined, ListItem> & {
  popularItems?: string[];
};

export type ListItem = {
  itemId: string;
  key: string;
  label: string;
  caption: string | undefined;
  providerId: string;
  providerName: string;
  serviceName: string;
  serviceId: string;
  version: string;
};

export type ProviderFilter = { id: string; name: string };

export type ServiceFilter = { id: string; name: string; version: string };

const transformProviders: TransformMethod<ApiDescriptor, ProviderFilter> = (
  { providerName, providerShortName, description },
  { id }
) => ({
  id,
  name: providerShortName,
  key: `${providerName}-${id}`,
  label: providerName,
  providerId: id,
  caption: description,
});

const transformOperations: TransformMethod<ApiServiceBaseOperationDescriptor, ListItem> = (
  { operationName, description, providerId, providerName, serviceId, serviceName, version, documentation },
  { id }
) => ({
  key: `${providerId}-${serviceId}-${operationName}-${id}`,
  itemId: id,
  label: providerId === CloudFlowProvider.DoiT ? description || operationName : operationName,
  caption: providerId === CloudFlowProvider.DoiT ? documentation || description : description,
  providerId,
  providerName,
  serviceId,
  serviceName,
  version,
});

const transformQueryResults = ({
  operationName,
  description,
  providerId,
  providerName,
  serviceId,
  serviceName,
  version,
  documentation,
}: Operation): ListItem => ({
  key: `${providerId}-${serviceId}-${operationName}`,
  itemId: operationName,
  label: providerId === CloudFlowProvider.DoiT ? description || operationName : operationName,
  caption: providerId === CloudFlowProvider.DoiT ? documentation || description : description,
  providerId,
  providerName,
  serviceName,
  serviceId,
  version,
});

const servicesQuery = getCollection(CloudflowEngine).doc("integrations").collection("apis");

export function useActionSearchDialogStateMachine(
  onOperationSelect: OnOperationSelectFn,
  provider: ProviderFilter | undefined,
  isProviderLoading: boolean
) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<ListItem[]>([]);
  const [service, setService] = useState<ServiceFilter>();
  const [browsedCollection, setBrowsedCollection] = useState<ActiveQuery | undefined>();

  const showingQueryResults = query.trim() !== "";

  const goBackToServices = useCallback(() => {
    setService(undefined);
    setQuery("");
  }, [setService, setQuery]);

  const transformServices = useCallback<TransformMethod<ApiServiceDescriptor, ListItem>>(
    ({ serviceName, latestVersion, description, documentation }, { id }) => ({
      key: `${provider?.id}-${serviceName}-${id}`,
      itemId: serviceName,
      label: serviceName,
      caption: description || documentation, // NOSONAR
      providerId: provider?.id ?? "",
      providerName: provider?.name ?? "",
      serviceId: id,
      serviceName,
      version: latestVersion,
    }),
    [provider?.id, provider?.name]
  );

  const operationsQuery = useMemo(() => {
    if (!service?.id || !service?.version || !provider?.id) {
      return;
    }

    return servicesQuery
      .doc(provider.id)
      .collection("services")
      .doc(service.id)
      .collection("versions")
      .doc(service.version)
      .collection("operations");
  }, [provider?.id, service?.id, service?.version]);

  const servicesQeury = useMemo(() => {
    if (!provider?.id) {
      return;
    }

    return servicesQuery.doc(provider.id).collection("services");
  }, [provider?.id]);

  const onListItemClick = useCallback(
    ({ itemId, providerId, serviceId, version, serviceName, label }: ListItem) => {
      if (showingQueryResults || operationsQuery !== undefined) {
        onOperationSelect({
          operationName: itemId,
          provider: providerId,
          service: serviceId,
          version,
          operationLabel: label,
        });
      } else if (servicesQeury !== undefined && provider?.id !== undefined) {
        setService({ id: serviceId, name: serviceName, version });
      }
    },
    [onOperationSelect, operationsQuery, showingQueryResults, servicesQeury, provider?.id]
  );

  useEffect(() => {
    switch (true) {
      case showingQueryResults:
        setBrowsedCollection(undefined);
        break;
      case operationsQuery !== undefined:
        setBrowsedCollection({
          query: operationsQuery,
          options: {
            transform: transformOperations as TransformMethod<DocumentData, ListItem>,
            orderBy: ["operationName"],
          },
        });
        break;
      case servicesQeury !== undefined && provider?.id !== undefined:
        setBrowsedCollection({
          query: servicesQeury,
          options: {
            orderBy: ["serviceName"],
            transform: transformServices as TransformMethod<DocumentData, ListItem>,
          },
          popularItems: popularServices[provider.id],
        });
        break;
      case provider?.id === undefined && isProviderLoading:
        setBrowsedCollection(undefined);
        break;
      default:
        throw new Error("unknown state");
    }
  }, [
    isProviderLoading,
    onOperationSelect,
    operationsQuery,
    provider?.id,
    servicesQeury,
    showingQueryResults,
    transformServices,
  ]);

  const [queryResults, loadingQuery] = useOperationsQuery(
    transformQueryResults,
    query.trim(),
    provider?.id,
    service?.id
  );

  const {
    items: browseItems,
    isLoading: loadingBrowseItems,
    handlePrevPage: loadPrevPage,
    handleNextPage: loadNextPage,
    hasNextPage,
    hasPrevPage,
  } = usePaginatedQuery(browsedCollection ? browsedCollection : { query: undefined, options: { orderBy: [""] } });

  const [popularItems] = useCollectionDataOnce(
    browsedCollection?.popularItems
      ? browsedCollection.query?.where(browsedCollection.options.orderBy[0], "in", browsedCollection.popularItems)
      : undefined,
    { transform: browsedCollection?.options.transform }
  );

  const isLoading = (showingQueryResults && loadingQuery) || (!showingQueryResults && loadingBrowseItems);

  useEffect(() => {
    if (showingQueryResults && !loadingQuery && queryResults) {
      setItems(queryResults);
    } else if (!showingQueryResults && !loadingBrowseItems && browseItems) {
      setItems(browseItems);
    } else {
      setItems([]);
    }
  }, [browseItems, loadingBrowseItems, loadingQuery, queryResults, showingQueryResults]);

  return {
    query,
    setQuery,
    isLoading,
    items,
    popularItems,
    loadPrevPage,
    loadNextPage,
    disablePrevPage: !hasPrevPage,
    disableNextPage: !hasNextPage,
    onListItemClick,
    provider,
    service,
    goBackToServices,
    showingQueryResults,
    setService,
  };
}

export function useProviderData(providerId: string) {
  return useDocumentData(servicesQuery.doc(providerId), {
    transform: transformProviders,
  });
}
